import Image from 'next/image';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { providerImgSrc } from 'src/utils/image';

import { CLIENT_PORTAL_MONOGRAM_COLORS } from './constants';
import { isPresent } from './utils';

type InstitutionAvatarSize = 'sm' | 'md' | 'lg';

type InstitutionAvatarProps = {
  readonly name: string;
  readonly logoUrl: string | null;
  readonly shape?: 'tile' | 'circle';
  readonly size?: InstitutionAvatarSize;
};

const TILE_CLASS = 'flex shrink-0 items-center justify-center overflow-hidden';

const SHAPE_CLASSES: Record<'tile' | 'circle', string> = {
  tile: 'rounded-md',
  circle: 'rounded-full',
};

const SIZE_CLASSES: Record<InstitutionAvatarSize, string> = {
  sm: 'size-7',
  md: 'size-9',
  lg: 'size-11',
};

const SIZE_PIXELS: Record<InstitutionAvatarSize, number> = {
  sm: 28,
  md: 36,
  lg: 44,
};

const MONOGRAM_VARIANT: Record<InstitutionAvatarSize, 'caption' | 'subtitle-sm' | 'subtitle-lg'> = {
  sm: 'caption',
  md: 'subtitle-sm',
  lg: 'subtitle-lg',
};

const INSET_CLASSES: Record<InstitutionAvatarSize, string> = {
  sm: 'p-1',
  md: 'p-1.5',
  lg: 'p-1.5',
};

function monogram(name: string): string {
  const initial = name.trim().charAt(0);
  return initial === '' ? '?' : initial.toUpperCase();
}

function monogramColor(name: string): string {
  let seed = 0;
  for (let index = 0; index < name.length; index += 1) {
    seed += name.charCodeAt(index);
  }
  return CLIENT_PORTAL_MONOGRAM_COLORS[seed % CLIENT_PORTAL_MONOGRAM_COLORS.length] ?? 'var(--chart-1)';
}

export function InstitutionAvatar({ name, logoUrl, shape = 'tile', size = 'sm' }: InstitutionAvatarProps) {
  const src = isPresent(logoUrl) ? providerImgSrc(logoUrl) : undefined;
  const shapeClass = SHAPE_CLASSES[shape];
  const sizeClass = SIZE_CLASSES[size];

  if (!isPresent(src)) {
    return (
      <Text
        variant={MONOGRAM_VARIANT[size]}
        color="light"
        className={cn(TILE_CLASS, shapeClass, sizeClass, 'font-semibold')}
        style={{ backgroundColor: monogramColor(name) }}
        render={<span />}
      >
        {monogram(name)}
      </Text>
    );
  }

  return (
    <Image
      src={src}
      alt=""
      width={SIZE_PIXELS[size]}
      height={SIZE_PIXELS[size]}
      className={cn(
        'shrink-0 border border-border-light bg-card object-contain',
        shapeClass,
        sizeClass,
        INSET_CLASSES[size],
      )}
      unoptimized
    />
  );
}
